// The account kept in the browser, so a second visit opens at once and asks Firestore only for what changed since
// (the kit keeps the same in a file: FirestoreBackend.Cache). One entry per account, in IndexedDB.
//
// Firestore's own persistent cache is on as well, but only for its queue of writes made offline (and so several tabs
// share one connection): listening to whole collections from it would read every document again whenever a listener
// was away for more than about half an hour, which is what the kit's design avoids.
//
// What is kept: the records as the sync holds them, the person's document, shares and placements, which share each
// shared record came from, and how far each was read. Stored values the browser can't keep as they are (a Timestamp
// in a field the web doesn't know, say) are kept with a tag and brought back as what they were, so they are written
// back unchanged.

import { isPlainObject } from "../core/records";
import type { Account } from "./account";
import type { StoredValueCodec } from "./remote";

/** Raised when what is kept changes shape: an older entry is then not used, and everything is read again. */
export const CACHE_FORMAT = 1;

export interface CachedAccount {
  format: number;
  /** The web version that wrote it: after an update everything is read once more (records an older one couldn't read). */
  appVersion: string;
  account: Account;
  /** The newest `modified` seen in the person's own data, from the server's clock (ms). */
  syncedUntil?: number;
  /** The same for each share. */
  shareSyncedUntil: Record<string, number>;
  /** Which share each shared record lives in, by record id. */
  containers: Record<string, string>;
  /** The last moment this browser was in step with the server (docs/64, ms); absent from versions before it was kept. */
  inStepAt?: number;
}

export interface AccountCache {
  read(uid: string): Promise<CachedAccount | null>;
  write(uid: string, cached: CachedAccount): Promise<void>;
  delete(uid: string): Promise<void>;
}

// MARK: - Keeping values

const TAG = "\u0000tag";

/** A copy that only holds what IndexedDB and JSON keep: Dates and stored values as tagged plain values. */
export function freeze(value: unknown, codec: StoredValueCodec): unknown {
  if (value === undefined || value === null || typeof value !== "object") return value;
  if (value instanceof Date) return { [TAG]: "date", value: value.getTime() };
  if (Array.isArray(value)) return value.map((item) => freeze(item, codec));
  if (isPlainObject(value)) {
    const entries = Object.entries(value).filter(([, inner]) => inner !== undefined).map(([key, inner]) => [key, freeze(inner, codec)]);
    const result = Object.fromEntries(entries);
    // A map that happens to hold the tag's key is kept as a map.
    return TAG in value ? { [TAG]: "map", value: result } : result;
  }
  const stored = codec.freeze(value);
  if (stored) return { [TAG]: stored.tag, value: stored.value };
  // Anything else that holds seconds and nanoseconds is a moment.
  const { seconds, nanoseconds } = value as { seconds?: unknown; nanoseconds?: unknown };
  if (typeof seconds === "number" && typeof nanoseconds === "number") return { [TAG]: "timestamp", value: [seconds, nanoseconds] };
  return value;
}

/** The values `freeze` kept, as they were. */
export function thaw(value: unknown, codec: StoredValueCodec): unknown {
  if (value === null || typeof value !== "object") return value;
  if (Array.isArray(value)) return value.map((item) => thaw(item, codec));
  const fields = value as Record<string, unknown>;
  const tag = fields[TAG];
  if (typeof tag === "string") {
    if (tag === "date") return new Date(fields.value as number);
    if (tag === "map") {
      return Object.fromEntries(Object.entries(fields.value as Record<string, unknown>).map(([key, inner]) => [key, thaw(inner, codec)]));
    }
    return codec.thaw(tag, fields.value);
  }
  return Object.fromEntries(Object.entries(fields).map(([key, inner]) => [key, thaw(inner, codec)]));
}

// MARK: - IndexedDB

const DATABASE = "todolix-web";
const STORE = "accounts";

/** The cache in IndexedDB. `factory` is the browser's `indexedDB` (tests pass fake-indexeddb's). */
export function indexedDBCache(codec: StoredValueCodec, factory: IDBFactory = globalThis.indexedDB): AccountCache {
  let opened: Promise<IDBDatabase> | undefined;
  const open = () => {
    opened ??= new Promise<IDBDatabase>((resolve, reject) => {
      const request = factory.open(DATABASE, 1);
      request.onupgradeneeded = () => {
        if (!request.result.objectStoreNames.contains(STORE)) request.result.createObjectStore(STORE);
      };
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error ?? new Error("IndexedDB could not be opened"));
      request.onblocked = () => reject(new Error("IndexedDB is blocked by another tab"));
    });
    // A failed open is tried again next time.
    opened.catch(() => { opened = undefined; });
    return opened;
  };
  const run = async <T>(mode: IDBTransactionMode, body: (store: IDBObjectStore) => IDBRequest<T> | undefined): Promise<T | undefined> => {
    const db = await open();
    return new Promise<T | undefined>((resolve, reject) => {
      const transaction = db.transaction(STORE, mode);
      const request = body(transaction.objectStore(STORE));
      transaction.oncomplete = () => resolve(request?.result);
      transaction.onerror = () => reject(transaction.error ?? new Error("IndexedDB failed"));
      transaction.onabort = () => reject(transaction.error ?? new Error("IndexedDB aborted"));
    });
  };
  return {
    async read(uid) {
      try {
        const stored = await run<unknown>("readonly", (store) => store.get(uid));
        return stored === undefined ? null : (thaw(stored, codec) as CachedAccount);
      } catch {
        // No cache (a private window, blocked storage): the account is read from the server.
        return null;
      }
    },
    async write(uid, cached) {
      await run("readwrite", (store) => store.put(freeze(cached, codec), uid));
    },
    async delete(uid) {
      await run("readwrite", (store) => store.delete(uid));
    },
  };
}

/** A cache in memory, kept frozen as IndexedDB would keep it: for the tests and the demo. */
export function memoryCache(codec: StoredValueCodec): AccountCache & { entries: Map<string, unknown> } {
  const entries = new Map<string, unknown>();
  return {
    entries,
    async read(uid) {
      const stored = entries.get(uid);
      // Through JSON, so anything IndexedDB couldn't keep either would show in the tests.
      return stored === undefined ? null : (thaw(JSON.parse(JSON.stringify(stored)), codec) as CachedAccount);
    },
    async write(uid, cached) {
      entries.set(uid, freeze(cached, codec));
    },
    async delete(uid) {
      entries.delete(uid);
    },
  };
}
